import React, { useState, useEffect } from 'react';
// material
import { Icon } from '@iconify/react';
import plusFill from '@iconify-icons/eva/plus-fill';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { styled } from '@mui/material/styles';
// components
import {
  Box,
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  Paper,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Link as RouterLink } from 'react-router-dom';
// components
import CustomPagination from '../components/CustomPagination';
import CreateButton from '../components/CreateButton';
import MoreUsers from '../components/dashboard/MoreUsers';
import PageUser from '../components/dashboard/PageUser';
import { usePrivilege } from '../PrivilegeContext';
import AdminMoreMenu from '../sections/@dashboard/user/AdminMoreMenu';
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import SnackBar from '../components/Snackbar';
import {
  APIDeleteUser,
  APIGetAdminUser,
  APIGetOperatorAdminAssignment,
  APIGetOperator,
} from '../utils/apiCalled';

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.email.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

const SUPER_ADMIN_TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: '' },
];

const OPERATOR_ADMIN_TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'operator', label: 'Operator', alignRight: false },
  { id: '' },
];

const tableStyle = {
  border: '0.063rem solid #EAECF0',
  borderTop: '0.094rem solid #EAECF0',
  borderBottom: '0.094rem solid #EAECF0',
  boxShadow: '0rem 0.063rem 0.188rem rgba(16, 24, 40, 0.1), 0rem 0.063rem 0.125rem rgba(16, 24, 40, 0.06)',
  borderRadius: '0.75rem',
};

const loadingStyle = {
  position: 'absolute',
  zIndex: 10,
  top: '35%',
  left: '55%',
  transform: 'translate(-50%, -50%)',
};

export default function User() {
  const [page, setPage] = useState(0);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [editState, setEditState] = useState(false);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openFilterPopUp, setOpenFilterPopUp] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [detailsPage, setDetailsPage] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [variant, setVariant] = useState('');
  const [mainMsg, setMainMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [succMsg, setSuccMsg] = useState('');
  const [getUser, setGetUser] = useState([]);
  const [modalTriggerAPICalled, setModalTriggerAPICalled] = useState(false);
  const [openMoreUser, setOpenMoreUser] = useState(false);
  const [openPageUser, setOpenPageUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [value, setValue] = useState(0);

  const { role: userRole } = usePrivilege();

  const theme = useTheme();

  const [operatorsMap, setOperatorsMap] = useState({});

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const result = await APIGetOperator(() => {});
        if (result?.data) {
          const map = {};
          result.data.forEach((op) => {
            map[op._id] = op.operatorName;
          });
          setOperatorsMap(map);
        }
      } catch (error) {
        console.error('Error fetching operators:', error);
      }
    };

    fetchOperators();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getAPICalled = async (role, email) => {
    try {
      setLoading(true);
      const [usersResult, assignmentsResult] = await Promise.all([
        APIGetAdminUser(setLoading),
        APIGetOperatorAdminAssignment(setLoading),
      ]);
      if (usersResult && usersResult.status === 200 && Array.isArray(usersResult.data)) {
        const allUsers = usersResult.data;
        const assignmentsMap = {};

        if (assignmentsResult && assignmentsResult.status === 200
          && Array.isArray(assignmentsResult.data)) {
          assignmentsResult.data.forEach((assignment) => {
            assignmentsMap[assignment.adminId] = assignment.assignedOperators;
          });
        }

        const mergedUsers = allUsers.map((user) => ({
          ...user,
          assignedOperators: assignmentsMap[user._id] || [],
        }));

        let filteredUsers;
        if (role === '101') {
          filteredUsers = mergedUsers.filter((user) => user.privileges?.role === 101);
        } else {
          filteredUsers = mergedUsers.filter((user) => user.privileges?.role !== 101);
        }
        setGetUser(filteredUsers);
      } else {
        setGetUser([]);
        setOpen(true);
        setVariant('error');
        setMainMsg('Error Encountered');
        setErrMsg('Error!, Please try again');
      }
    } catch (err) {
      setGetUser([]);
      setOpen(true);
      setVariant('error');
      setMainMsg('Error Encountered');
      setErrMsg('Error!', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentRole = value === 0 ? '101' : '102';
    getAPICalled(currentRole, filterName);
    if (modalTriggerAPICalled) {
      setModalTriggerAPICalled(false);
    }
  }, [value, filterName, modalTriggerAPICalled]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = getUser.map((n) => n.email);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    const newFilterName = event.target.value;
    setFilterName(newFilterName);
    setPage(0);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setGetUser([]);
    setLoading(true);
    setFilterName('');
    setPage(0);
    const newRole = newValue === 0 ? '101' : '102';
    getAPICalled(newRole, '');
  };

  const handleSnackBarClose = () => {
    setOpen(false);
  };

  const handleDelete = async (data) => {
    try {
      const result = await APIDeleteUser(setLoading, data._id);
      if (result.status === 200) {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('success');
        setMainMsg('User Successfully Deleted');
        setSuccMsg(`User ${data.firstName} ${data.lastName} has been successfully deleted`);
        setModalTriggerAPICalled(true);
        setOpenMoreUser(false);
      } else {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('error');
        setMainMsg('Error Deleting User');
        setErrMsg('Error deleting user!, Please try again');
      }
    } catch (err) {
      setLoading(false);
      setDeleteModal(false);
      setOpen(true);
      setVariant('error');
      setMainMsg('Error Deleting User');
      setErrMsg('Error deleting user!, Please try again');
    }
  };

  const handleOpenDeleteModal = () => {
    setDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  const handleOpenMoreUsers = (user) => {
    setSelectedUser({ ...user, operatorNames: Array.isArray(user.assignedOperators) ? user.assignedOperators.map((opId) => operatorsMap[opId] || 'Unknown') : [] });
    setOpenMoreUser(true);
    setOpenPageUser(false);
  };

  const handleCloseMoreUsers = () => {
    setSelectedUser(false);
    setOpenMoreUser(false);
    setOpenPageUser(false);
  };

  const handleOpenPageUsers = (user) => {
    setSelectedUser(user);
    setOpenMoreUser(false);
    setOpenPageUser(true);
  };

  const handleOpenPageUsersCreate = () => {
    setOpenMoreUser(false);
    setOpenPageUser(true);
  };

  const StyledTab = styled(Tab)(({ theme: tabTheme }) => ({
    textTransform: 'none',
    fontWeight: tabTheme.typography.fontWeightBold,
    fontSize: '0.9rem',
    color: tabTheme.palette.grey[500],
    '&.Mui-selected': {
      color: tabTheme.palette.primary.main,
    },
  }));

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - getUser.length) : 0;
  const filteredUsers = applySortFilter(getUser, getComparator(order, orderBy), filterName);
  const isUserNotFound = filteredUsers.length === 0;

  return (
    <Page title="Super Admin">
      <Container maxWidth="xl" sx={{ mt: (!openMoreUser && !openPageUser) ? 12 : 0 }}>
        <SnackBar
          open={open}
          alert={variant}
          handleClose={handleSnackBarClose}
          errorMsg={errMsg}
          successMsg={succMsg}
          mainMsg={mainMsg}
        />
        {(!openMoreUser && !openPageUser)
          && (
          <>
            <Box sx={{
              pb: 5, display: 'flex', flexDirection: 'column', alignItems: 'left',
            }}
            >
              <Typography variant="h3">{value === 0 ? 'Super Admin' : 'Operator Admin'}</Typography>
              <Tabs value={value} onChange={handleChange} aria-label="admin tabs">
                <StyledTab label="Super Admin" />
                <StyledTab label="Operator Admin" />
              </Tabs>
            </Box>
            {loading && <CircularProgress color="success" sx={loadingStyle} />}
            <Box sx={tableStyle}>
              <Box sx={{
                mr: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
              >
                <UserListToolbar
                  numSelected={selected.length}
                  filterName={filterName}
                  onFilterName={handleFilterByName}
                  popupfilter={setOpenFilterPopUp}
                  labelName="Search Email..."
                  displayFilter={false}
                />
                <CreateButton type={value === 0 ? 'Super Admin' : 'Operator Admin'} handleOpen={handleOpenPageUsersCreate} />
              </Box>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={value === 0 ? SUPER_ADMIN_TABLE_HEAD : OPERATOR_ADMIN_TABLE_HEAD}
                      rowCount={getUser.length}
                      numSelected={selected.length}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                    />
                    <TableBody>
                      {filteredUsers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                          const {
                            _id, firstName, lastName, email, phone, operator,
                          } = row;
                          const isItemSelected = selected.indexOf(_id) !== -1;

                          return (
                            <TableRow
                              hover
                              key={_id}
                              tabIndex={-1}
                              role="checkbox"
                              selected={isItemSelected}
                              aria-checked={isItemSelected}
                            >
                              <TableCell align="left" component="th" scope="row" onClick={() => handleOpenMoreUsers(row)}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Typography variant="body2" sx={{ color: theme.palette.grey[900] }}>
                                    {row.firstName}
                                    {' '}
                                    {row.lastName}
                                  </Typography>
                                </Stack>
                              </TableCell>
                              <TableCell align="left" onClick={() => handleOpenMoreUsers(row)}><Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>{row.email}</Typography></TableCell>
                              <TableCell align="left" onClick={() => handleOpenMoreUsers(row)}><Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>{row.phone}</Typography></TableCell>
                              {value === 1 && (
                              <TableCell align="left" onClick={() => handleOpenMoreUsers(row)}>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {Array.isArray(row.assignedOperators)
                                  && row.assignedOperators.length > 0 ? (
                                      row.assignedOperators.map((opId) => (
                                        <Label
                                          key={opId}
                                          variant="soft"
                                          color="primary"
                                        >
                                          {operatorsMap[opId] || 'Unknown'}
                                        </Label>
                                      ))
                                    ) : (
                                      <Typography variant="body2" sx={{ color: theme.palette.grey[500] }}>
                                        No Operators Assigned
                                      </Typography>
                                    )}
                                </Stack>
                              </TableCell>
                              )}
                              <TableCell align="right">
                                <AdminMoreMenu
                                  handleSeeMore={handleOpenMoreUsers}
                                  handleEditOpen={handleOpenPageUsers}
                                  data={row}
                                  handleDeleteOpen={handleOpenDeleteModal}
                                  handleClose={handleCloseDeleteModal}
                                  deleteModal={deleteModal}
                                  handleDelete={handleDelete}
                                  setModalTriggerAPICalled={setModalTriggerAPICalled}
                                  setSuccMsg={setSuccMsg}
                                  setErrMsg={setErrMsg}
                                  setVariant={setVariant}
                                  setOpen={setOpen}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
              {isUserNotFound && <SearchNotFound searchQuery={filterName} />}
              <CustomPagination
                page={page}
                totalNum={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
              />
            </Box>
          </>
          )}
        {openMoreUser && (
        <MoreUsers
          handleCloseMoreUsers={handleCloseMoreUsers}
          user={selectedUser}
          handleDelete={handleDelete}
          deleteModal={deleteModal}
          handleOpenDeleteModal={handleOpenDeleteModal}
          handleCloseDeleteModal={handleCloseDeleteModal}
          handleOpenPageUsers={handleOpenPageUsers}
          role={userRole}
          value={value}
        />
        )}
        {openPageUser && (
        <PageUser
          handleCloseMoreUsers={handleCloseMoreUsers}
          user={selectedUser}
          setOpen={setOpen}
          setSuccMsg={setSuccMsg}
          setErrMsg={setErrMsg}
          setVariant={setVariant}
          setMainMsg={setMainMsg}
          setModalTriggerAPICalled={setModalTriggerAPICalled}
          role={userRole}
          value={value}
        />
        )}
      </Container>
    </Page>
  );
}
